import { useId, useMemo, useState, type HTMLAttributes } from 'react';
import { InfoCircle, SearchNormal1 } from 'iconsax-reactjs';
import { cx } from '../internal/cx';
import { useControllableState } from '../internal/useControllableState';
import { Checkbox } from './Checkbox';
import type { ChoiceOption, GroupFieldProps } from './RadioGroup';
import styles from './Form.module.css';

export interface CheckboxGroupOption extends ChoiceOption {
  /** Plain-text label used for filtering when `label` is not a string. */
  searchText?: string;
}

export interface CheckboxGroupProps
  extends GroupFieldProps, Omit<HTMLAttributes<HTMLFieldSetElement>, 'onChange' | 'defaultValue'> {
  options: CheckboxGroupOption[];
  value?: string[];
  defaultValue?: string[];
  onChange?: (value: string[]) => void;
  orientation?: 'vertical' | 'horizontal';
  /** Show a filter box. Defaults to true when there are more than 8 options. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Max height of the option list before it scrolls (e.g. "16rem"). */
  maxListHeight?: string;
  /** Show "Select all" / "Clear" shortcuts. */
  showBulkActions?: boolean;
}

const EMPTY: string[] = [];

/**
 * Multiple selection as a fieldset of checkboxes (optionally filterable). Use for choosing
 * several grades, programs, schools, etc.
 */
export function CheckboxGroup({
  label,
  hideLabel = false,
  hint,
  error,
  required = false,
  disabled = false,
  options,
  value: valueProp,
  defaultValue = EMPTY,
  onChange,
  orientation = 'vertical',
  searchable,
  searchPlaceholder = 'Filter options',
  maxListHeight,
  showBulkActions = false,
  className,
  ...rest
}: CheckboxGroupProps) {
  const baseId = useId();
  const hintId = hint ? `${baseId}-hint` : undefined;
  const errorId = error ? `${baseId}-error` : undefined;
  const countId = `${baseId}-count`;
  const [value, setValue] = useControllableState(valueProp, defaultValue, onChange);
  const [filter, setFilter] = useState('');
  const isSearchable = searchable ?? options.length > 8;

  const selected = useMemo(() => new Set(value), [value]);
  const visibleOptions = useMemo(() => {
    const q = filter.trim().toLocaleLowerCase();
    if (!q) return options;
    return options.filter((option) => {
      const text = option.searchText ?? (typeof option.label === 'string' ? option.label : '');
      return text.toLocaleLowerCase().includes(q);
    });
  }, [filter, options]);

  function toggle(optionValue: string, checked: boolean) {
    if (checked) {
      // Preserve option order in the emitted value.
      const next = new Set(value);
      next.add(optionValue);
      setValue(options.map((o) => o.value).filter((v) => next.has(v)));
    } else {
      setValue(value.filter((v) => v !== optionValue));
    }
  }

  return (
    <fieldset
      className={cx(styles.group, className)}
      aria-describedby={cx(hintId, errorId) || undefined}
      disabled={disabled || undefined}
      {...rest}
    >
      <legend className={cx(styles.label, styles.legend, hideLabel && 'sr-only')}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        )}
      </legend>
      {hint && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {(isSearchable || showBulkActions) && (
        <div className={styles.groupToolbar}>
          {isSearchable && (
            <div className={cx(styles.control, styles['control-sm'], styles.groupSearch)}>
              <span className={styles.adornment} aria-hidden="true">
                <SearchNormal1 size={16} color="currentColor" />
              </span>
              <input
                type="search"
                className={styles.input}
                value={filter}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                aria-describedby={countId}
                onChange={(event) => setFilter(event.target.value)}
              />
            </div>
          )}
          {showBulkActions && (
            <div className={styles.groupBulk}>
              <button
                type="button"
                className={styles.textButton}
                onClick={() =>
                  setValue(
                    options.filter((o) => !o.disabled || selected.has(o.value)).map((o) => o.value),
                  )
                }
              >
                Select all
              </button>
              <button type="button" className={styles.textButton} onClick={() => setValue([])}>
                Clear
              </button>
            </div>
          )}
        </div>
      )}
      <p id={countId} className={styles.groupCount} aria-live="polite">
        {value.length} selected
        {filter ? `, ${visibleOptions.length} of ${options.length} options shown` : ''}
      </p>
      <div
        className={cx(
          styles.choices,
          orientation === 'horizontal' && styles.choicesRow,
          maxListHeight && styles.choicesScroll,
        )}
        style={maxListHeight ? { maxHeight: maxListHeight } : undefined}
      >
        {visibleOptions.map((option) => (
          <Checkbox
            key={option.value}
            id={`${baseId}-${option.value}`}
            label={option.label}
            description={option.description}
            checked={selected.has(option.value)}
            disabled={option.disabled}
            invalid={error ? true : undefined}
            onCheckedChange={(checked) => toggle(option.value, checked)}
          />
        ))}
        {visibleOptions.length === 0 && <p className={styles.hint}>No matching options.</p>}
      </div>
      {error && (
        <p id={errorId} className={styles.error}>
          <InfoCircle size={14} color="currentColor" variant="Bold" aria-hidden="true" />
          <span>{error}</span>
        </p>
      )}
    </fieldset>
  );
}
